import { repairTextArtifacts } from '@/lib/text-encoding';
import type { HomepageSettingsDoc } from '@/types/platform';
import { isSettingsObject, SettingsInputError, type SettingsPatch } from './settings-patch';

type Rule = 'text' | 'href' | 'image' | 'boolean' | 'number' | 'ids' | 'tone' | { [key: string]: Rule } | readonly [Rule];
const cta: Rule = { label: 'text', href: 'href', tone: 'tone', enabled: 'boolean' };
const card = { icon: 'text', title: 'text', description: 'text', enabled: 'boolean', sortOrder: 'number' } as const;
const schema: Record<string, Rule> = {
  homeHero: { eyebrow: 'text', title: 'text', description: 'text', primaryCta: cta, secondaryCta: cta, tertiaryCta: cta, imageUrl: 'image', imageAlt: 'text' },
  homeTrustItems: [{ ...card, href: 'href' }],
  homeFeatured: { enabled: 'boolean', sectionTitle: 'text', sectionDescription: 'text', titleIds: 'ids', fallbackFeatured: 'boolean' },
  homeMomentsTitle: 'text', homeMomentCards: [{ ...card, href: 'href' }],
  homePromiseBlock: { enabled: 'boolean', title: 'text', body: 'text', secondaryBody: 'text', ctaLabel: 'text', ctaHref: 'href', quoteTitle: 'text', quoteBody: 'text' },
  homeBookPath: { enabled: 'boolean', title: 'text', description: 'text', titleIds: 'ids', fallbackLatest: 'boolean' },
  homeKnotSectionTitle: 'text', homeKnotCards: [{ ...card, href: 'href', titleId: 'text' }],
  homeCollection: { enabled: 'boolean', title: 'text', description: 'text', ctaLabel: 'text', ctaHref: 'href', imageUrl: 'image', imageAlt: 'text', titleIds: 'ids' },
  homeNotHereTitle: 'text', homeNotHereDescription: 'text', homeNotHereCards: [card],
  homeJournal: { enabled: 'boolean', title: 'text', description: 'text', postIds: 'ids', ctaLabel: 'text', ctaHref: 'href', fallbackLatest: 'boolean' },
  homeNewsletter: { enabled: 'boolean', title: 'text', description: 'text', imageUrl: 'image', imageAlt: 'text', sourceSlug: 'text', submitLabel: 'text' },
  homeSeo: { metaTitle: 'text', metaDescription: 'text', ogImageUrl: 'image', ogImageAlt: 'text' },
  shopFeaturedCollectionTitleIds: 'ids', shopBundleTitleIds: 'ids',
  shopBenefitItems: [{ title: 'text', description: 'text', icon: 'text' }],
};
export const SITE_IMAGE_FIELDS = ['siteLogo', 'leadMagnetImage', 'firstChapterHeroImage', 'firstChapterPanelImage', 'aboutImage', 'blogHeroImage', 'storefrontBannerImage', 'defaultTitleCover'] as const;
for (const field of SITE_IMAGE_FIELDS) { schema[`${field}Url`] = 'image'; schema[`${field}Alt`] = 'text'; }
for (const field of ['shopHeroEyebrow', 'shopHeroTitle', 'shopHeroDescription', 'shopHeroCtaLabel', 'shopFeaturedCollectionTitle', 'shopFeaturedCollectionDescription', 'shopFeaturedCollectionCtaLabel', 'shopBundleTitle', 'shopBundleDescription', 'shopNewsletterTitle', 'shopNewsletterDescription']) schema[field] = 'text';
schema.shopHeroCtaHref = 'href';
for (const field of ['shopFeaturedCollectionImage', 'shopBundleImage', 'shopNewsletterImage']) { schema[`${field}Url`] = 'image'; schema[`${field}Alt`] = 'text'; }

export const HOMEPAGE_LEGACY_ALIASES = {
  heroImageUrl: ['homeHero', 'imageUrl'], heroImageAlt: ['homeHero', 'imageAlt'],
  featuredTitleIds: ['homeFeatured', 'titleIds'], showFeaturedOnHomepage: ['homeFeatured', 'enabled'],
  sectionTitle: ['homeFeatured', 'sectionTitle'], sectionDescription: ['homeFeatured', 'sectionDescription'],
} as const;

function clean(value: unknown, rule: Rule, path: string, strict: boolean, inArray = false): unknown {
  const invalid = () => { if (strict) throw new SettingsInputError(`Invalid setting: ${path}.`); return undefined; };
  if (value === undefined) return undefined;
  if (value === null) return strict ? (inArray ? invalid() : null) : undefined;
  if (Array.isArray(rule)) {
    if (!Array.isArray(value) || value.length > 200) return invalid();
    return value.map((item, index) => clean(item, rule[0], `${path}.${index}`, strict, true)).filter(item => item !== undefined);
  }
  if (typeof rule === 'object') {
    if (!isSettingsObject(value)) return invalid();
    if (!strict && 'description' in rule && !Object.hasOwn(value, 'description') && typeof value.text === 'string') value = { ...value, description: value.text };
    const result: SettingsPatch = {};
    for (const [key, item] of Object.entries(value as SettingsPatch)) {
      const childRule = (rule as Record<string, Rule>)[key];
      if (!Object.hasOwn(rule, key)) { if (strict) throw new SettingsInputError(`Unknown setting: ${path}.${key}.`); continue; }
      const next = clean(item, childRule, `${path}.${key}`, strict, inArray);
      if (next !== undefined) result[key] = next;
    }
    return result;
  }
  if (rule === 'boolean') return typeof value === 'boolean' ? value : invalid();
  if (rule === 'number') return typeof value === 'number' && Number.isFinite(value) ? value : invalid();
  if (rule === 'ids') {
    if (!Array.isArray(value) || value.length > 200 || value.some(item => typeof item !== 'string')) return invalid();
    return [...new Set(value.map(item => item.trim()).filter(Boolean))];
  }
  if (typeof value !== 'string' || value.length > 20000) return invalid();
  const text = repairTextArtifacts(value.trim());
  if (rule === 'tone') return ['green', 'light', 'peach'].includes(text) ? text : invalid();
  if (rule === 'href' || rule === 'image') {
    if (!text) return '';
    if (/[\\\u0000-\u0020]/.test(text)) return invalid();
    if (rule === 'href' && text.startsWith('#')) return text;
    if (text.startsWith('/') && !text.startsWith('//')) return text;
    try { if (new URL(text).protocol === 'https:') return text; } catch { /* invalid URL */ }
    return invalid();
  }
  return text;
}

export type HomepageEditorScope = 'homepage' | 'shop' | 'images' | 'homepage-shop';
export function sanitizeHomepagePatch(value: unknown, scope: HomepageEditorScope): SettingsPatch {
  if (!['homepage', 'shop', 'images', 'homepage-shop'].includes(scope) || !isSettingsObject(value)) throw new SettingsInputError('A scoped settings patch is required.');
  const patch = clean(value, schema, 'homepage', true) as SettingsPatch;
  for (const [key, item] of Object.entries(patch)) {
    const allowed = scope === 'homepage-shop' ? key.startsWith('home') || key.startsWith('shop') || key.startsWith('storefrontBannerImage') : scope === 'homepage' ? key.startsWith('home') : scope === 'shop' ? key.startsWith('shop') || key.startsWith('storefrontBannerImage') : SITE_IMAGE_FIELDS.some(field => key === `${field}Url` || key === `${field}Alt`) || (key === 'homeHero' && isSettingsObject(item) && Object.keys(item).every(field => ['imageUrl', 'imageAlt'].includes(field)));
    if (!allowed) throw new SettingsInputError(`Setting ${key} is outside the ${scope} editor.`);
  }
  if (isSettingsObject(patch.homeNewsletter)) {
    for (const field of ['sourceSlug', 'submitLabel']) {
      const value = patch.homeNewsletter[field];
      if (typeof value === 'string' && (!value || value.length > 100)) throw new SettingsInputError(`Newsletter ${field} must contain 1–100 characters; use null to restore its default.`);
    }
  }
  return patch;
}

/** Tolerant legacy read, with nested canonical fields winning even when empty/false.
 * No defaults are added here. This result is never used as a write patch. */
export function sanitizeHomepageSettings(value: unknown): HomepageSettingsDoc {
  const raw = isSettingsObject(value) ? value : {};
  const normalized = { ...raw };
  for (const [legacy, [section, field]] of Object.entries(HOMEPAGE_LEGACY_ALIASES)) {
    const nested = isSettingsObject(normalized[section]) ? normalized[section] : {};
    if (!Object.hasOwn(nested, field) && raw[legacy] !== undefined) normalized[section] = { ...nested, [field]: raw[legacy] };
  }
  const result = clean(normalized, schema, 'homepage', false) as HomepageSettingsDoc;
  // Read aliases keep existing secondary page consumers compatible; never writable.
  for (const [legacy, [section, field]] of Object.entries(HOMEPAGE_LEGACY_ALIASES)) {
    const nested = result[section] as SettingsPatch | undefined;
    if (nested?.[field] !== undefined) (result as SettingsPatch)[legacy] = nested[field];
  }
  return result;
}
